import { createAction, createReducer } from 'redux-act';
import { useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { IGlobalState } from '../index';
import { gatewayV2 } from '../../helpers/client';

export type DeliveryConfirmationResult = Awaited<ReturnType<typeof gatewayV2.sendDeliveryConfirmation>>;

type IState = {
    lastConfirmationResult: DeliveryConfirmationResult | null;
    isSmsSending: boolean;
    retryTimerSecondsLeft: number;
    isTimerRunning?: boolean;
};

const initialState: IState = {
    lastConfirmationResult: null,
    isSmsSending: false,
    retryTimerSecondsLeft: 0,
};

export const setSmsRetryTimerIsRunning = createAction<boolean>('start sms retry timer');
export const updateRetryTimerSecondsLeft = createAction<number>('update timer seconds left');
export const setIsSmsSending = createAction<boolean>('set is sms sending');
export const setLastConfirmationResult = createAction<DeliveryConfirmationResult | null>(
    'set last confirmation result',
);

export const deliverySmsConfirmationReducer = createReducer<IState>({}, initialState)
    .on(updateRetryTimerSecondsLeft, (state, secondsLeft) => ({
        ...state,
        retryTimerSecondsLeft: secondsLeft,
    }))
    .on(setSmsRetryTimerIsRunning, (state, isRunning) => ({
        ...state,
        isTimerRunning: isRunning,
    }))
    .on(setIsSmsSending, (state, isSending) => ({
        ...state,
        isSmsSending: isSending,
    }))
    .on(setLastConfirmationResult, (state, result) => ({
        ...state,
        lastConfirmationResult: result,
    }));

export const retryTimerSecondsLeftSelector = (state: IGlobalState) =>
    state.deliverySmsConfirmation.retryTimerSecondsLeft;

export const retryTimerIsRunningSelector = (state: IGlobalState) => state.deliverySmsConfirmation.isTimerRunning;

export const isSmsSendingSelector = (state: IGlobalState) => state.deliverySmsConfirmation.isSmsSending;

export const lastConfirmationResultSelector = (state: IGlobalState) =>
    state.deliverySmsConfirmation.lastConfirmationResult;

export const useRetryTimerSecondsLeft = () => {
    return useSelector(retryTimerSecondsLeftSelector);
};

export const useRetryTimerIsRunning = () => {
    return useSelector(retryTimerIsRunningSelector);
};

export const useIsSmsSending = () => {
    return useSelector(isSmsSendingSelector);
};

export const useLastConfirmationResult = () => {
    return useSelector(lastConfirmationResultSelector);
};

export const getRetryTimerSecondsLeftThunk = () => (_: Dispatch, getState: () => IGlobalState) => {
    return retryTimerSecondsLeftSelector(getState());
};
